import { useState } from "react"
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table"

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"

import { GoPlus } from "react-icons/go"

import { Pagination } from "./Pagination"
import Modal from "@/components/Modal"
import Form from "./Form"
import { LuListFilter } from "react-icons/lu"
import FormFilter from "./FormFilter"
import { RiFileExcel2Fill } from "react-icons/ri"
import useApiBaseUrl from "@/hooks/useApiBaseUrl"
import { useSearchParams } from "react-router-dom"

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[]
  data: TData[]
}

export function DataTable<TData, TValue>({
  columns,
  data,
}: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = useState<SortingState>([])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({})
  const [rowSelection, setRowSelection] = useState({})

  const [showInsert, setShowInsert] = useState(false)
  const [showFilter, setShowFilter] = useState(false)

  const { apiBaseUrl } = useApiBaseUrl()
  const [searchParams] = useSearchParams()

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
  })

  return (
    <div>
      <div className="flex items-center justify-between gap-3 py-4">
        <Input
          placeholder="Filter by nama ruang..."
          value={table.getColumn("nama_ruang")?.getFilterValue() as string}
          onChange={(event) =>
            table.getColumn("nama_ruang")?.setFilterValue(event.target.value)
          }
          className="max-w-sm"
        />
        <div className="flex gap-x-3">
          <Button
            variant={"outline"}
            className="flex items-center gap-x-3"
            onClick={() => {
              const idsSysPeriodeParams = searchParams.get("ids_sys_periode")
              const tanggalCbtParams = searchParams.get("tanggal_cbt")
              const jenisRuangParams = searchParams.get("jenis_ruang")
              const isPenuhParams = searchParams.get("is_penuh")

              if (idsSysPeriodeParams)
                searchParams.set("ids_sys_periode", idsSysPeriodeParams)
              if (tanggalCbtParams)
                searchParams.set("tanggal_cbt", tanggalCbtParams)
              if (jenisRuangParams)
                searchParams.set("jenis_ruang", jenisRuangParams)
              if (isPenuhParams) searchParams.set("is_penuh", isPenuhParams)

              const url = new URL(
                `${apiBaseUrl}/setting/ruang-cbt-detail/export?${searchParams.toString()}`
              )

              const params = new URLSearchParams(window.location.search)
              url.search = params.toString()

              window.open(url.toString(), "_blank")
            }}
          >
            Export
            <RiFileExcel2Fill />
          </Button>

          <Button
            className="flex items-center gap-x-3"
            onClick={() => setShowInsert(true)}
          >
            Insert
            <GoPlus />
          </Button>
          <Button
            variant={"outline"}
            className="flex items-center gap-x-3"
            onClick={() => setShowFilter(true)}
          >
            Filter
            <LuListFilter />
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="ml-auto">
                Columns
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {table
                .getAllColumns()
                .filter((column) => column.getCanHide())
                .map((column) => {
                  return (
                    <DropdownMenuCheckboxItem
                      key={column.id}
                      className="capitalize"
                      checked={column.getIsVisible()}
                      onCheckedChange={(value) =>
                        column.toggleVisibility(!!value)
                      }
                    >
                      {column.id}
                    </DropdownMenuCheckboxItem>
                  )
                })}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <div className="border rounded-xl">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  )
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <br />
      <div>
        <Pagination table={table} />
      </div>

      <Modal show={showInsert} onClose={() => setShowInsert(false)}>
        <Form closeModal={() => setShowInsert(false)} method="POST" />
      </Modal>

      <Modal show={showFilter} onClose={() => setShowFilter(false)} width="2xl">
        <FormFilter closeModal={() => setShowFilter(false)} />
      </Modal>
    </div>
  )
}
